<template>
  <div
    v-if="showLoadingDot"
    class="absolute top-0 left-0 bg-black z-[9999] h-1 w-1 opacity-10"
  />

  <NuxtLoadingIndicator
    :color="colors.primary"
    :throttle="500"
  />

  <div class="relative overflow-clip">
    <PageTransition />

    <App />

    <LazyCookieConsent hydrate-on-idle />

    <client-only>
      <LazySkewProtectionNewVersionNotification hydrate-on-idle />
    </client-only>
  </div>
</template>

<script setup>
import { callWithNuxt } from '#app'

import colors from '#tailwind-config/theme/colors'

useConfigStore()

const route = useRoute()
const nuxtApp = useNuxtApp()

const whitelabelStore = useWhitelabelStore()

const { $isSolWebsite, $locale, $t } = useNuxtApp()

const { urls: localeURLs } = useLocale()

const rootStore = useRootStore()

const {
  isUserReseller,
} = storeToRefs(rootStore)

const localeLangInfo = localeToLangInfo($locale)

const router = useRouter()

const showLoadingDot = ref(false)

onServerPrefetch(async () => {
  if ($isSolWebsite && route.query?.wl) {
    whitelabelStore.setWhiteLabel(route.query?.wl)
  }

  await rootStore.handleAgentCookie(route.query?.a)

  const fetchPromises = [
    callWithNuxt(nuxtApp, rootStore.fetchAgentCall),
    callWithNuxt(nuxtApp, rootStore.fetchTopNavbar),
    callWithNuxt(nuxtApp, rootStore.fetchAlert),
  ]

  if (!$isSolWebsite) {
    fetchPromises.push(callWithNuxt(nuxtApp, rootStore.fetchTripTypesSidebar))
  }

  try {
    await Promise.all(fetchPromises)
  } catch (error) {
    throw createError({
      statusCode: 500,
      statusMessage: error.message,
    })
  }
})

/**
 * Init cookies on client side for prerendered pages
 */
const initCookies = async () => {
  if (route.query?.a && typeof route.query.a === 'string') {
    await rootStore.handleAgentCookie(route.query.a)
  }
}

onMounted(async () => {
  await initCookies()

  if ($isSolWebsite) {
    whitelabelStore.checkAgentCookie()
  }

  watch(
    isUserReseller,
    (value) => {
      if (!value) {
        return
      }

      rootStore.fetchTopNavbar()
      rootStore.fetchAgentCall()
    },
    { immediate: true }
  )
})

useHead({
  titleTemplate: () => `%s - ${$t('siteTitleBrand')}`,

  title: () => $t('siteTitleBrand'),

  link: [
    {
      rel: 'canonical',
      href: () => `${localeURLs.BASE_URL}${route.path}`,
    },
  ],

  meta: [
    {
      name: 'author',
      content: () => $t('siteTitleBrand'),
    },
    {
      property: 'og:title',
      content: () => $t('siteTitleBrand'),
    },
    {
      property: 'og:locale',
      content: () => localeLangInfo.iso.replace('-', '_'),
    },
    {
      property: 'og:site_name',
      content: () => $t('siteTitleBrand'),
    },
    {
      property: 'og:url',
      content: () => `${localeURLs.BASE_URL}${route.path}`,
    },
  ],
})

useSchemaOrg([
  defineWebSite({
    name: $t('siteTitleBrand'),
    description: $t('homeDescription'),
    inLanguage: localeLangInfo.iso,
  }),
])

router.beforeEach((_to, _from, next) => {
  showLoadingDot.value = true

  // Let Vue update DOM and browser paint before continuing
  setTimeout(() => next(), 50)
})

router.afterEach(() => {
  showLoadingDot.value = false
})
</script>

<style lang="scss">
@import "@/assets/scss/style.scss";
</style>
